/**
 * House ownership (lordship) counted from the Lagna.
 *
 * Most planets rule two signs, so from any given Lagna they own two houses.
 * The Sun rules only Leo and the Moon only Cancer, so each owns one house.
 * Rahu and Ketu are shadow points rather than sign lords; by the convention
 * used here Rahu is associated with Aquarius and Ketu with Scorpio, and they
 * are shown as a sign rather than a house number.
 */

const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

const PLANET_OWNS = {
  Sun: ['Leo'],
  Moon: ['Cancer'],
  Mars: ['Aries', 'Scorpio'],
  Mercury: ['Gemini', 'Virgo'],
  Jupiter: ['Sagittarius', 'Pisces'],
  Venus: ['Taurus', 'Libra'],
  Saturn: ['Capricorn', 'Aquarius'],
  Rahu: ['Aquarius'],
  Ketu: ['Scorpio'],
};

// The nodes are shown as a sign, never as a house number.
const NODES = new Set(['Rahu', 'Ketu']);

function ordinal(n) {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
  return `${n}${suffix}`;
}

/**
 * @returns {{houses: number[], signs: string[], isNode: boolean, label: string}|null}
 * null when the Lagna is unknown, so callers can show a placeholder rather
 * than a wrong house number.
 */
export function ownershipFromLagna(planet, lagnaSign) {
  const owned = PLANET_OWNS[planet];
  if (!owned) return null;

  const isNode = NODES.has(planet);
  if (isNode) {
    return { houses: [], signs: owned, isNode: true, label: owned.join(', ') };
  }

  if (!SIGNS.includes(lagnaSign)) return null;

  const houses = owned
    .map((sign) => ((SIGNS.indexOf(sign) - SIGNS.indexOf(lagnaSign) + 12) % 12) + 1)
    .sort((a, b) => a - b);

  return {
    houses,
    signs: owned,
    isNode: false,
    label: houses.map(ordinal).join(', '),
  };
}

export { PLANET_OWNS };
export default ownershipFromLagna;
